/**
 * roomStats.ts - Room Emotion Stats & Grade System
 *
 * Each room has 4 emotion stats: fear, thrill, atmosphere, fun (0-100 each).
 * Stats are calculated from: room type base + ghost bonus + prop bonus + upgrade multiplier.
 * Grade is determined by total stats sum.
 */

import type { Room } from './state.js';
import type { GameStateData } from './state.js';
import { ROOM_TYPES } from './data/rooms.js';
import type { RoomTypeId } from './data/rooms.js';
import { GHOST_DEFS } from './data/ghosts.js';
import { PROP_DEFS } from './data/props.js';
import { calcActiveSynergies, aggregateSynergyEffects } from './data/roomSynergies.js';

// ── Types ──────────────────────────────────────────────────

export interface RoomStats {
  fear: number;       // 0-100
  thrill: number;     // 0-100
  atmosphere: number; // 0-100
  fun: number;        // 0-100
}

export type RoomGrade = 'S' | 'A' | 'B' | 'C' | 'D' | 'F';

// ── Calculation ────────────────────────────────────────────

/**
 * Calculate the 4 emotion stats for a room by summing:
 * 1. Room type base stats
 * 2. Assigned ghost's stat bonus
 * 3. All installed props' stat bonuses (scaled by prop level)
 * 4. Upgrade level multiplier (+2% per level)
 */
export function calcRoomStats(room: Room, state: GameStateData): RoomStats {
  const roomDef = ROOM_TYPES[room.type];
  const base = roomDef.baseStats;

  // Start from room base stats
  let fear = base.fear;
  let thrill = base.thrill;
  let atmosphere = base.atmosphere;
  let fun = base.fun;

  // Ghost bonus
  if (room.assignedGhostId) {
    const ghost = state.ghosts.find((g) => g.id === room.assignedGhostId);
    if (ghost) {
      const ghostDef = GHOST_DEFS[ghost.characterId];
      if (ghostDef.statBonus) {
        fear += ghostDef.statBonus.fear;
        thrill += ghostDef.statBonus.thrill;
        atmosphere += ghostDef.statBonus.atmosphere;
        fun += ghostDef.statBonus.fun;
      }
    }
  }

  // Prop bonuses (each prop's bonus scales with its installed level)
  for (const rp of room.props) {
    const propDef = PROP_DEFS[rp.propId];
    if (propDef && propDef.statBonus) {
      const lvl = rp.level;
      fear += (propDef.statBonus.fear ?? 0) * lvl;
      thrill += (propDef.statBonus.thrill ?? 0) * lvl;
      atmosphere += (propDef.statBonus.atmosphere ?? 0) * lvl;
      fun += (propDef.statBonus.fun ?? 0) * lvl;
    }
  }

  // Upgrade level multiplier: +2% per level
  const upgradeMult = 1 + room.upgradeLevel * 0.02;

  // Room synergy bonuses
  const builtRoomTypes = state.building
    .map((f) => f.room?.type)
    .filter((t): t is RoomTypeId => t != null);
  const synergies = calcActiveSynergies(builtRoomTypes);
  const effects = aggregateSynergyEffects(synergies);
  const synergyFearMult = 1 + effects.fear_boost / 100;
  const synergyThrillMult = 1 + effects.thrill_boost / 100;
  const synergyAtmoMult = 1 + effects.atmosphere_boost / 100;
  const synergyFunMult = 1 + effects.fun_boost / 100;

  return {
    fear: Math.min(100, Math.round(fear * upgradeMult * synergyFearMult)),
    thrill: Math.min(100, Math.round(thrill * upgradeMult * synergyThrillMult)),
    atmosphere: Math.min(100, Math.round(atmosphere * upgradeMult * synergyAtmoMult)),
    fun: Math.min(100, Math.round(fun * upgradeMult * synergyFunMult)),
  };
}

// ── Grade ──────────────────────────────────────────────────

/**
 * Calculate room grade from total stats sum (max 400).
 */
export function calcRoomGrade(stats: RoomStats): RoomGrade {
  const total = stats.fear + stats.thrill + stats.atmosphere + stats.fun;
  if (total >= 300) return 'S';
  if (total >= 230) return 'A';
  if (total >= 160) return 'B';
  if (total >= 100) return 'C';
  if (total >= 50) return 'D';
  return 'F';
}

/**
 * Calculate stat-based satisfaction multiplier for a visitor.
 * Matches room stats against visitor type preferences.
 * Returns a value typically between 0.5 and 1.5.
 */
export function calcStatSatisfaction(
  stats: RoomStats,
  preference: { fear: number; thrill: number; atmosphere: number; fun: number },
): number {
  const weightSum = preference.fear + preference.thrill + preference.atmosphere + preference.fun;
  if (weightSum <= 0) return 1.0;

  const weighted =
    (stats.fear / 100) * preference.fear +
    (stats.thrill / 100) * preference.thrill +
    (stats.atmosphere / 100) * preference.atmosphere +
    (stats.fun / 100) * preference.fun;

  return weighted / weightSum;
}
